import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { PlatformIdentityProvider } from "@/hooks/usePlatformIdentity";
import { portalForUser, ROLE_PORTAL_MAP } from "@/lib/roles";
import Landing from "@/pages/Landing";
import { SignIn, SignUp } from "@/pages/Auth";
import Navigator from "@/pages/Navigator";
import { HomePage, TasksPage, PlanPage, DocumentsPage, AppointmentsPage, MessagesPage } from "@/pages/Dashboard";
import Marketplace, { MarketplaceDetail } from "@/pages/Marketplace";
import Services from "@/pages/Services";
import Pathways from "@/pages/Pathways";
import { Employers, Partners, About, Contact } from "@/pages/Static";
import { CaseloadPage, HandoffsPage, StaffParticipantPage } from "@/pages/Staff";
import AdminConsole from "@/pages/Admin";
import COSAppCenter from "@/pages/COSAppCenter";
import TalentProfile from "@/pages/TalentProfile";
import PartnerReferrals from "@/pages/Partner";
import StaffReferralsPage from "@/pages/StaffReferrals";
import { PortalPicker, ResetPassword, AccessDenied } from "@/pages/Access";
import {
  PartnerDashboard, PartnerPrograms,
  TrainingDashboard, GovernmentDashboard, FundingDashboard, VolunteerDashboard,
} from "@/pages/Portals";
import { EmployerOnboarding, PartnerOnboarding, OnboardingHub } from "@/pages/Onboarding";
import {
  EmployerDashboard, EmployerJobOrders, EmployerJobOrderDetail,
  EmployerCandidates, EmployerHiringActivity, EmployerProfile,
} from "@/pages/EmployerPortal";
import { BDDashboard, BDOrgList, BDOrgDetail, StaffLeadsPage } from "@/pages/StaffBD";
import { StaffJobOrderQueue } from "@/pages/StaffJobOrders";
import {
  IntelligenceDashboard, IntelligenceDetail, IntelligenceRules,
  IntelligenceThresholds, IntelligenceSources, IntelligenceAnalytics,
  IntelligenceExecDashboard, IntelligenceInstitutionalMemory,
  IntelligenceForecast, IntelligenceExecBrief,
} from "@/pages/IntelligenceCenter";
import { OpportunityCenter, OpportunityArticle } from "@/pages/OpportunityCenter";

const RequireAuth = ({ roles, moduleId, moduleName, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-white">Loading...</div>;
  if (!user) return <Navigate to="/signin" replace/>;
  // Force first-login password reset
  if (user.must_reset_password && window.location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace/>;
  }
  const userRoles = user.roles && user.roles.length ? user.roles : [user.role];
  const allowed = !roles || userRoles.some(r => roles.includes(r)) || userRoles.includes("super_admin");
  if (!allowed) return <AccessDenied moduleId={moduleId || "unknown"} moduleName={moduleName}/>;
  return children;
};

const StartGate = () => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return null;
  if (!user) return <SignUp/>;
  // Honor Candidate Network intent — participants who signed up via /start?intent=find_work
  // land on the Candidate Profile with a welcome banner instead of the generic participant home.
  const intent = new URLSearchParams(loc.search).get("intent");
  const roles = user.roles && user.roles.length ? user.roles : [user.role];
  if (intent === "find_work" && roles.includes("participant")) {
    return <Navigate to="/app/talent-profile?welcome=1" replace/>;
  }
  return <Navigate to={portalForUser(user)} replace/>;
};

// Convenience: partner-family roles all use the Partner portal.
const PARTNER_ROLES = ["partner", "community_partner"];
const EMPLOYER_ROLES = ["employer"];
const TRAINING_ROLES = ["training_provider", "instructor"];
const GOV_ROLES = ["government_agency"];
const FUND_ROLES = ["funding_org"];
const VOL_ROLES = ["volunteer"];

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <PlatformIdentityProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors/>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing/>}/>
            <Route path="/services" element={<Services/>}/>
            <Route path="/marketplace" element={<Marketplace/>}/>
            <Route path="/marketplace/:id" element={<MarketplaceDetail/>}/>
            <Route path="/pathways" element={<Pathways/>}/>
            <Route path="/employers" element={<Employers/>}/>
            <Route path="/partners" element={<Partners/>}/>
            <Route path="/about" element={<About/>}/>
            <Route path="/contact" element={<Contact/>}/>

            <Route path="/signin" element={<SignIn/>}/>
            <Route path="/signup" element={<Navigate to="/start" replace/>}/>
            <Route path="/start" element={<StartGate/>}/>

            {/* Public self-service onboarding wizards */}
            <Route path="/onboarding" element={<OnboardingHub/>}/>
            <Route path="/onboarding/employer" element={<EmployerOnboarding/>}/>
            <Route path="/onboarding/partner" element={<PartnerOnboarding/>}/>

            {/* Unified auth flows */}
            <Route path="/portal" element={<RequireAuth><PortalPicker/></RequireAuth>}/>
            <Route path="/reset-password" element={<RequireAuth><ResetPassword/></RequireAuth>}/>
            <Route path="/access-denied" element={<AccessDenied moduleId="unknown"/>}/>

            {/* Participant */}
            <Route path="/app/navigator" element={<RequireAuth roles={["participant"]} moduleId="ai_navigator" moduleName="AI Opportunity Navigator"><Navigator/></RequireAuth>}/>
            <Route path="/app/home" element={<RequireAuth roles={["participant"]} moduleId="participant_home"><HomePage/></RequireAuth>}/>
            <Route path="/app/talent-profile" element={<RequireAuth roles={["participant"]} moduleId="talent_profile" moduleName="Candidate Profile"><TalentProfile/></RequireAuth>}/>
            <Route path="/app/tasks" element={<RequireAuth roles={["participant"]} moduleId="tasks"><TasksPage/></RequireAuth>}/>
            <Route path="/app/plan" element={<RequireAuth roles={["participant"]} moduleId="opportunity_plan"><PlanPage/></RequireAuth>}/>
            <Route path="/app/documents" element={<RequireAuth roles={["participant"]} moduleId="documents"><DocumentsPage/></RequireAuth>}/>
            <Route path="/app/appointments" element={<RequireAuth roles={["participant"]} moduleId="appointments"><AppointmentsPage/></RequireAuth>}/>
            <Route path="/app/messages" element={<RequireAuth roles={["participant"]} moduleId="messages"><MessagesPage/></RequireAuth>}/>

            {/* Staff */}
            <Route path="/staff/caseload" element={<RequireAuth roles={["staff","admin"]} moduleId="staff_caseload" moduleName="Staff Caseload"><CaseloadPage/></RequireAuth>}/>
            <Route path="/staff/handoffs" element={<RequireAuth roles={["staff","admin"]} moduleId="handoff_packets"><HandoffsPage/></RequireAuth>}/>
            <Route path="/staff/participants/:id" element={<RequireAuth roles={["staff","admin"]} moduleId="participant_record"><StaffParticipantPage/></RequireAuth>}/>
            <Route path="/staff/referrals" element={<RequireAuth roles={["staff","admin"]} moduleId="referrals_inbox"><StaffReferralsPage/></RequireAuth>}/>
            <Route path="/staff/bd" element={<RequireAuth roles={["staff","admin"]} moduleId="bd_crm" moduleName="Business Development CRM"><BDDashboard/></RequireAuth>}/>
            <Route path="/staff/bd/organizations" element={<RequireAuth roles={["staff","admin"]} moduleId="bd_crm"><BDOrgList/></RequireAuth>}/>
            <Route path="/staff/bd/organizations/:id" element={<RequireAuth roles={["staff","admin"]} moduleId="bd_crm"><BDOrgDetail/></RequireAuth>}/>
            <Route path="/staff/leads" element={<RequireAuth roles={["staff","admin"]} moduleId="public_leads"><StaffLeadsPage/></RequireAuth>}/>
            <Route path="/staff/job-orders" element={<RequireAuth roles={["staff","admin"]} moduleId="job_orders_queue" moduleName="Job Orders Queue"><StaffJobOrderQueue/></RequireAuth>}/>

            {/* Opportunity Intelligence Center (staff) */}
            <Route path="/staff/intelligence" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic"><IntelligenceDashboard/></RequireAuth>}/>
            <Route path="/staff/intelligence/rules" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_rules"><IntelligenceRules/></RequireAuth>}/>
            <Route path="/staff/intelligence/thresholds" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_thresholds"><IntelligenceThresholds/></RequireAuth>}/>
            <Route path="/staff/intelligence/sources" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_sources"><IntelligenceSources/></RequireAuth>}/>
            <Route path="/staff/intelligence/analytics" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_analytics"><IntelligenceAnalytics/></RequireAuth>}/>
            <Route path="/staff/intelligence/exec-dashboard" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_exec"><IntelligenceExecDashboard/></RequireAuth>}/>
            <Route path="/staff/intelligence/institutional-memory" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_memory"><IntelligenceInstitutionalMemory/></RequireAuth>}/>
            <Route path="/staff/intelligence/forecast" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_forecast"><IntelligenceForecast/></RequireAuth>}/>
            <Route path="/staff/intelligence/exec-brief" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_brief"><IntelligenceExecBrief/></RequireAuth>}/>
            <Route path="/staff/intelligence/:id" element={<RequireAuth roles={["staff","admin","super_admin"]} moduleId="oic_detail"><IntelligenceDetail/></RequireAuth>}/>

            {/* Public Opportunity Center */}
            <Route path="/opportunities" element={<OpportunityCenter/>}/>
            <Route path="/opportunities/:slug" element={<OpportunityArticle/>}/>

            {/* Admin */}
            <Route path="/admin" element={<RequireAuth roles={["admin"]} moduleId="admin_console"><AdminConsole/></RequireAuth>}/>
            <Route path="/admin/apps" element={<RequireAuth roles={["admin","super_admin"]} moduleId="cos_app_center" moduleName="COS App Center"><COSAppCenter/></RequireAuth>}/>

            {/* Employer Portal */}
            <Route path="/portal/employer" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="employer_portal" moduleName="Employer Portal"><EmployerDashboard/></RequireAuth>}/>
            <Route path="/portal/employer/profile" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="employer_profile"><EmployerProfile/></RequireAuth>}/>
            <Route path="/portal/employer/job-orders" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="job_orders"><EmployerJobOrders/></RequireAuth>}/>
            <Route path="/portal/employer/job-orders/:id" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="job_orders"><EmployerJobOrderDetail/></RequireAuth>}/>
            <Route path="/portal/employer/candidates" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="candidates"><EmployerCandidates/></RequireAuth>}/>
            <Route path="/portal/employer/hiring-activity" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="hiring_activity"><EmployerHiringActivity/></RequireAuth>}/>
            <Route path="/portal/employer/documents" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="documents"><DocumentsPage/></RequireAuth>}/>
            <Route path="/portal/employer/messages" element={<RequireAuth roles={EMPLOYER_ROLES} moduleId="messages"><MessagesPage/></RequireAuth>}/>

            {/* Partner Portal (community_partner + legacy partner) */}
            <Route path="/portal/partner" element={<RequireAuth roles={PARTNER_ROLES} moduleId="partner_portal" moduleName="Partner Portal"><PartnerDashboard/></RequireAuth>}/>
            <Route path="/portal/partner/programs" element={<RequireAuth roles={PARTNER_ROLES} moduleId="programs"><PartnerPrograms/></RequireAuth>}/>
            <Route path="/portal/partner/documents" element={<RequireAuth roles={PARTNER_ROLES} moduleId="documents"><DocumentsPage/></RequireAuth>}/>
            <Route path="/portal/partner/messages" element={<RequireAuth roles={PARTNER_ROLES} moduleId="messages"><MessagesPage/></RequireAuth>}/>
            <Route path="/partner/referrals" element={<RequireAuth roles={[...PARTNER_ROLES, "employer"]} moduleId="referrals"><PartnerReferrals/></RequireAuth>}/>

            {/* Training / Government / Funding / Volunteer portals */}
            <Route path="/portal/training" element={<RequireAuth roles={TRAINING_ROLES} moduleId="training_portal" moduleName="Training Provider Portal"><TrainingDashboard/></RequireAuth>}/>
            <Route path="/portal/training/documents" element={<RequireAuth roles={TRAINING_ROLES} moduleId="documents"><DocumentsPage/></RequireAuth>}/>
            <Route path="/portal/training/messages" element={<RequireAuth roles={TRAINING_ROLES} moduleId="messages"><MessagesPage/></RequireAuth>}/>
            <Route path="/portal/training/cohorts" element={<RequireAuth roles={TRAINING_ROLES} moduleId="cohorts"><TrainingDashboard/></RequireAuth>}/>

            <Route path="/portal/government" element={<RequireAuth roles={GOV_ROLES} moduleId="government_portal" moduleName="Government Portal"><GovernmentDashboard/></RequireAuth>}/>
            <Route path="/portal/government/documents" element={<RequireAuth roles={GOV_ROLES} moduleId="documents"><DocumentsPage/></RequireAuth>}/>
            <Route path="/portal/government/messages" element={<RequireAuth roles={GOV_ROLES} moduleId="messages"><MessagesPage/></RequireAuth>}/>
            <Route path="/portal/government/contracts" element={<RequireAuth roles={GOV_ROLES} moduleId="contracts"><GovernmentDashboard/></RequireAuth>}/>

            <Route path="/portal/funding" element={<RequireAuth roles={FUND_ROLES} moduleId="funding_portal" moduleName="Funding Portal"><FundingDashboard/></RequireAuth>}/>
            <Route path="/portal/funding/documents" element={<RequireAuth roles={FUND_ROLES} moduleId="documents"><DocumentsPage/></RequireAuth>}/>
            <Route path="/portal/funding/messages" element={<RequireAuth roles={FUND_ROLES} moduleId="messages"><MessagesPage/></RequireAuth>}/>
            <Route path="/portal/funding/grants" element={<RequireAuth roles={FUND_ROLES} moduleId="grants"><FundingDashboard/></RequireAuth>}/>

            <Route path="/portal/volunteer" element={<RequireAuth roles={VOL_ROLES} moduleId="volunteer_portal" moduleName="Volunteer Portal"><VolunteerDashboard/></RequireAuth>}/>
            <Route path="/portal/volunteer/opportunities" element={<RequireAuth roles={VOL_ROLES} moduleId="volunteer_opportunities"><VolunteerDashboard/></RequireAuth>}/>
            <Route path="/portal/volunteer/messages" element={<RequireAuth roles={VOL_ROLES} moduleId="messages"><MessagesPage/></RequireAuth>}/>

            <Route path="*" element={<Navigate to="/" replace/>}/>
          </Routes>
        </BrowserRouter>
        </PlatformIdentityProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
